'use client'

import { Check, ChevronDown, type LucideIcon } from 'lucide-react'
import Link from 'next/link'
import { forwardRef, memo, useCallback } from 'react'

import { ExternalIcon, isRedirectHref, isSectionActive } from './NavLink'
import { cn } from '@lib/cn'
import { commonIconProps } from '@lib/iconProps'
import { DropdownMenuItem } from '@ui/DropDownMenu'

export const navMenuContentCSS = 'min-w-56'

// DropdownMenuItem's own `[&_svg]:size-4` is a descendant selector, so it outbids any size set on the icon itself.
const navMenuItemCSS = 'cursor-pointer gap-2.5 px-2 py-1.5 text-base [&_svg]:size-4.5'

const navPillCSS = cn(
  'inline-flex items-center gap-1.5 self-center whitespace-nowrap rounded-full px-3 py-1.5 text-sm font-normal transition-colors',
  'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-ring',
)

const navPillStateCSS = (isActive: boolean) =>
  isActive ? 'bg-primary/10 text-primary' : 'text-foreground hover:bg-accent hover:text-secondary-foreground'

type NavMenuTriggerProps = React.ComponentPropsWithoutRef<'button'> & {
  icon: LucideIcon
  label: React.ReactNode
  isActive: boolean
}

// Radix mounts this through `<DropdownMenuTrigger asChild>`, so it has to forward both ref and props.
export const NavMenuTrigger = forwardRef<HTMLButtonElement, NavMenuTriggerProps>(
  ({ icon: Icon, label, isActive, className, ...props }, ref) => (
    <button
      ref={ref}
      type='button'
      className={cn('group', navPillCSS, navPillStateCSS(isActive), className)}
      {...props}
    >
      <Icon {...commonIconProps} className='size-4 shrink-0' />
      {label}
      <ChevronDown
        {...commonIconProps}
        className='size-3.5 shrink-0 transition-transform duration-200 group-data-[state=open]:rotate-180'
      />
    </button>
  ),
)
NavMenuTrigger.displayName = 'NavMenuTrigger'

type NavBarLinkProps = { href: string; title: string; pathname: string }

export const NavBarLink: React.FC<NavBarLinkProps> = memo(({ href, title, pathname }) => {
  const isRedirect = isRedirectHref(href)
  const isCurrentPage = !isRedirect && pathname === href

  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>) => {
      if (isCurrentPage) e.preventDefault()
    },
    [isCurrentPage],
  )

  return (
    <Link
      href={href}
      prefetch={false}
      onClick={handleClick}
      target={isRedirect ? '_blank' : undefined}
      className={cn(navPillCSS, navPillStateCSS(!isRedirect && isSectionActive(pathname, href)))}
    >
      {title}
      {isRedirect && <ExternalIcon />}
    </Link>
  )
})
NavBarLink.displayName = 'NavBarLink'

type NavMenuItemProps = {
  href: string
  title: string
  pathname: string
  /** Grouped nav only — classic rows stay text-only. */
  icon?: LucideIcon
}

export const NavMenuItem: React.FC<NavMenuItemProps> = memo(({ href, title, pathname, icon: Icon }) => {
  const isRedirect = isRedirectHref(href)
  const isActive = !isRedirect && isSectionActive(pathname, href)

  return (
    <DropdownMenuItem asChild className={navMenuItemCSS}>
      <Link
        href={href}
        prefetch={false}
        target={isRedirect ? '_blank' : undefined}
        className={cn(isActive && 'text-primary font-medium')}
      >
        {Icon && <Icon {...commonIconProps} className={cn(!isActive && 'text-foreground/70')} />}
        <span className='flex-1 text-nowrap'>{title}</span>
        {isActive && <Check {...commonIconProps} />}
        {isRedirect && <ExternalIcon />}
      </Link>
    </DropdownMenuItem>
  )
})
NavMenuItem.displayName = 'NavMenuItem'
